import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Star, MessageSquare, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RatingScreen = ({ isOpen, onClose, onComplete, tableId, customerId }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [googleUrl, setGoogleUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) { onComplete(); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reviews`, {
        rating, comment, table_id: tableId, customer_id: customerId || ''
      });
      if (res.data.google_review_url && rating >= 4) {
        setGoogleUrl(res.data.google_review_url);
        setSubmitted(true);
      } else {
        setSubmitted(true);
        setTimeout(() => { onComplete(); }, 1500);
      }
    } catch (e) {
      console.error(e);
      onComplete();
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleRedirect = () => {
    if (googleUrl) {
      window.open(googleUrl, '_blank');
    }
    setTimeout(() => { onComplete(); }, 500);
  };

  const handleClose = () => {
    if (!submitted) { onComplete(); return; }
    onComplete();
  };

  const stars = [1, 2, 3, 4, 5];
  const labels = ['', 'Pas bien', 'Bof', 'Correct', 'Tres bien !', 'Excellent !'];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-sm bg-[#0D0D0D] border border-[#333333] rounded-3xl p-0 overflow-hidden">
        {!submitted ? (
          <div className="px-6 py-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4A853] to-[#E16833] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-white text-xl font-bold mb-1">
              Comment c'etait ?
            </h2>
            <p className="text-[#A0A0A0] text-sm mb-6">
              Votre avis compte pour nous
            </p>

            {/* Stars */}
            <div className="flex justify-center gap-2 mb-2">
              {stars.map(s => (
                <button key={s}
                  onMouseEnter={() => setHoverRating(s)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(s)}
                  className="transition-transform hover:scale-110 active:scale-95"
                  data-testid={`star-${s}`}
                >
                  <Star className={`w-10 h-10 transition-colors ${
                    s <= (hoverRating || rating)
                      ? 'fill-[#D4A853] text-[#D4A853]'
                      : 'text-[#555555]'
                  }`} />
                </button>
              ))}
            </div>
            <p className="text-[#D4A853] text-sm font-medium h-5 mb-5">
              {labels[hoverRating || rating] || ''}
            </p>

            {/* Comment */}
            {rating > 0 && (
              <div className="mb-5">
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-[#555555]" />
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={rating >= 4 ? "Qu'avez-vous prefere ?" : "Comment pouvons-nous nous ameliorer ?"}
                    className="w-full bg-[#1A1A1A] border border-[#333333] rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-[#555555] resize-none h-20 focus:outline-none focus:border-[#D4A853]"
                    data-testid="review-comment"
                  />
                </div>
              </div>
            )}

            {/* Hint for 4-5 stars */}
            {rating >= 4 && (
              <div className="bg-[#1A1A1A] border border-[#D4A853]/30 rounded-xl px-4 py-3 mb-5">
                <p className="text-[#D4A853] text-xs font-medium flex items-center justify-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" />
                  Merci ! Vous serez redirige vers Google pour partager votre avis
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => onComplete()}
                className="flex-1 py-3 rounded-full border border-[#333333] text-[#A0A0A0] text-sm transition-colors hover:bg-[#1A1A1A]"
                data-testid="skip-rating-btn">
                Passer
              </button>
              <button onClick={handleSubmit} disabled={rating === 0 || submitting}
                className="flex-1 py-3 rounded-full bg-gradient-to-r from-[#D4A853] to-[#E16833] text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-40"
                data-testid="submit-rating-btn">
                {submitting ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-8 text-center">
            {rating >= 4 && googleUrl ? (
              <>
                <div className="w-16 h-16 bg-gradient-to-br from-[#4285F4] to-[#34A853] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white fill-white" />
                </div>
                <h2 className="text-white text-xl font-bold mb-2">
                  Merci beaucoup !
                </h2>
                <p className="text-[#A0A0A0] text-sm mb-6">
                  Partagez votre experience sur Google Reviews pour aider d'autres gourmands a nous decouvrir !
                </p>
                <button onClick={handleGoogleRedirect}
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#4285F4] via-[#34A853] to-[#FBBC05] text-white text-sm font-bold flex items-center justify-center gap-2 transition-all hover:opacity-90"
                  data-testid="google-review-redirect-btn">
                  <ExternalLink className="w-4 h-4" />
                  Poster sur Google Reviews
                </button>
                <button onClick={() => onComplete()}
                  className="mt-3 text-[#A0A0A0] text-xs underline"
                  data-testid="skip-google-btn">
                  Continuer sans poster
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-[#0A422D] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-white text-xl font-bold mb-2">
                  Merci pour votre retour
                </h2>
                <p className="text-[#A0A0A0] text-sm">
                  Nous prendrons en compte vos remarques
                </p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RatingScreen;
